import FullScreenModalState from '@/interfaces/FullScreenModal';
import { SyncModalState } from '@/interfaces/SyncModalState';
import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';
import { Component, computed, markRaw, ref } from 'vue';

export const useModalStore = defineStore('modal', () => {
  // 전체화면 모달 정보
  const fullScreenModalState = ref<FullScreenModalState>({
    isOpen: false,
    component: null,
    props: {},
    history: [],
  });
  // 동기 모달 정보
  const syncModalStack = ref<SyncModalState[]>([]);
  // 관리자 인증 모달 표시 여부
  const isAdminAuthModalOpen = ref(false);
  // 장바구니 모달 표시 여부
  const isCartModalOpen = ref(false);

  // 최상위 동기 모달 아이디
  const currentSyncModalId = computed(() => {
    return syncModalStack.value.length > 0 ? syncModalStack.value[syncModalStack.value.length - 1].id : null;
  });

  /**
   * 전체화면 모달 열기
   * @param component 모달에 표시할 컴포넌트
   * @param props 컴포넌트에 전달할 props
   */
  const openFullScreenModal = <T extends Component>(
    component: T,
    props: T extends new (...args: any) => { $props: infer P } ? (keyof P extends never ? undefined : P) : never,
    replace?: boolean,
  ): void => {
    if (fullScreenModalState.value.component && !replace) {
      fullScreenModalState.value.history.push({
        component: fullScreenModalState.value.component,
        props: fullScreenModalState.value.props,
      });
    }
    fullScreenModalState.value.isOpen = true;
    fullScreenModalState.value.component = markRaw(component);
    fullScreenModalState.value.props = (props || {}) as Record<string, unknown>;

    closeCartModal();
  };

  /**
   * 전체화면 모달 닫기
   */
  const closeFullScreenModal = () => {
    fullScreenModalState.value = {
      isOpen: false,
      component: null,
      props: {},
      history: [],
    };
  };

  /**
   * 전체화면 모달 뒤로가기
   */
  const goBackFullScreenModal = () => {
    if (fullScreenModalState.value.history.length > 0) {
      const prevModal = fullScreenModalState.value.history.pop();
      if (!prevModal) return;
      fullScreenModalState.value.component = markRaw(prevModal.component);
      fullScreenModalState.value.props = prevModal.props;
    } else {
      closeFullScreenModal();
    }
  };

  /**
   * 동기 모달을 열기
   * @param component 표시할 모달 컴포넌트
   * @param props 모달에 전달할 props
   * @returns 사용자의 입력 결과를 resolve/reject하는 Promise
   */
  const openSyncModalAsync = <TComponent extends Component, TResult>(
    component: TComponent,
    props: TComponent extends new (...args: any) => { $props: infer P } ? P : {},
  ): Promise<TResult> => {
    closeCartModal();

    return new Promise<TResult>((resolve, reject) => {
      syncModalStack.value.push({
        id: uuid(),
        component: markRaw(component),
        props: props || {},
        resolver: resolve,
        rejector: reject,
      });
    });
  };

  // 최상위 동기 모달 닫기
  const closeTopSyncModal = () => {
    console.log('closeTopSyncModal');
    if (syncModalStack.value.length > 0) syncModalStack.value.pop();
  };

  /**
   * 모달에서 확인 버튼 클릭 시 호출
   */
  const resolveSyncModal = <T>(value: T) => {
    console.log('resolveSyncModal');
    const topSyncModal = syncModalStack.value[syncModalStack.value.length - 1];
    if (topSyncModal) {
      topSyncModal.resolver(value);
      closeTopSyncModal();
    }
  };

  /**
   * 모달에서 취소 버튼 클릭 시 호출
   * @param reason 취소 사유
   */
  const rejectSyncModal = (reason?: any) => {
    console.log('rejectSyncModal');
    const topSyncModal = syncModalStack.value[syncModalStack.value.length - 1];
    if (topSyncModal) {
      topSyncModal.rejector(reason);
      closeTopSyncModal();
    }
  };

  /** 관리자 인증 모달 열기 */
  const openAdminAuthModal = () => (isAdminAuthModalOpen.value = true);
  /** 관리자 인증 모달 닫기 */
  const closeAdminAuthModal = () => (isAdminAuthModalOpen.value = false);

  /** 장바구니 모달 열기 */
  const openCartModal = () => (isCartModalOpen.value = true);
  /** 장바구니 모달 닫기 */
  const closeCartModal = () => (isCartModalOpen.value = false);

  /** 모든 모달 닫기 */
  const closeAllModal = () => {
    closeAdminAuthModal();
    closeCartModal();
    closeFullScreenModal();
    rejectSyncModal();
  };

  return {
    fullScreenModalState,
    syncModalStack,
    isAdminAuthModalOpen,
    isCartModalOpen,

    currentSyncModalId,

    openFullScreenModal,
    closeFullScreenModal,
    goBackFullScreenModal,
    openSyncModalAsync,
    resolveSyncModal,
    rejectSyncModal,

    openAdminAuthModal,
    closeAdminAuthModal,
    openCartModal,
    closeCartModal,
    closeAllModal,
  };
});
